import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { FileText, Upload, LogOut, Clock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatements();
  }, []);

  const loadStatements = async () => {
    try {
      const response = await api.get('/statements');
      setStatements(response.data);
    } catch (error) {
      toast.error('Failed to load statements');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-ledger-bone relative noise-overlay">
      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-black/10 bg-white">
          <div className="max-w-7xl mx-auto px-6 py-6 flex justify-between items-center">
            <h1 className="font-serif text-2xl font-bold tracking-tight text-ledger-green" data-testid="dashboard-title">Dashboard</h1>
            <div className="flex items-center gap-4">
              <span className="font-mono text-xs text-gray-600" data-testid="user-email">{user?.email}</span>
              <button
                onClick={handleLogout}
                className="font-mono text-xs text-gray-600 hover:text-ledger-green flex items-center gap-2"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-6 py-12">
          {/* Free Trial Status */}
          {user?.subscription_status !== 'active' && user?.free_uploads_remaining > 0 && (
            <div className="border-2 border-ledger-orange p-6 bg-ledger-orange/5 mb-8" data-testid="free-trial-banner">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-serif text-xl font-bold text-ledger-green mb-1">🎁 Free Trial Active</h2>
                  <p className="font-mono text-sm text-gray-700">
                    You have <span className="font-bold text-ledger-orange">{user.free_uploads_remaining} free extraction</span> remaining. Try it now!
                  </p>
                </div>
                <button
                  onClick={() => navigate('/pricing')}
                  className="bg-ledger-green text-white rounded-none hover:bg-ledger-green/90 uppercase tracking-widest text-xs py-2 px-4 font-mono whitespace-nowrap"
                  data-testid="view-plans-btn"
                >
                  View Plans
                </button>
              </div>
            </div>
          )}

          {/* Subscription Prompt (no free uploads left) */}
          {user?.subscription_status !== 'active' && user?.free_uploads_remaining <= 0 && (
            <div className="border-2 border-ledger-orange p-8 bg-white mb-8 text-center" data-testid="subscription-prompt">
              <h2 className="font-serif text-2xl font-bold text-ledger-green mb-2">You've Used Your Free Extraction!</h2>
              <p className="font-mono text-sm text-gray-600 mb-4">Subscribe now for unlimited statement conversions</p>
              <button
                onClick={() => navigate('/pricing')}
                className="bg-ledger-orange text-white rounded-none hover:bg-ledger-orange/90 uppercase tracking-widest text-xs py-3 px-6 font-mono"
                data-testid="view-pricing-btn"
              >
                View Pricing Plans
              </button>
            </div>
          )}

          {user?.subscription_status === 'active' && (
            <div className="border-2 border-green-500 p-4 bg-green-50 mb-8 flex items-center gap-3" data-testid="subscription-active">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div className="flex-1">
                <p className="font-mono text-sm text-green-900">
                  Subscription Active - {user.subscription_plan === 'monthly' ? 'Monthly Plan' : 'Annual Plan'}
                </p>
                {user.subscription_expires_at && (
                  <p className="font-mono text-xs text-green-700">
                    Renews on {new Date(user.subscription_expires_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Quick Upload */}
          {(user?.subscription_status === 'active' || user?.free_uploads_remaining > 0) && (
            <div className="border-2 border-ledger-green p-12 bg-white mb-12 text-center">
              <FileText className="w-16 h-16 text-ledger-green mx-auto mb-4" />
              <h2 className="font-serif text-3xl font-bold text-ledger-green mb-4">Upload a Statement</h2>
              <p className="font-mono text-sm text-gray-600 mb-6">
                {user?.subscription_status === 'active' 
                  ? 'Drop your PDF or image file to get started'
                  : `Use your free extraction - ${user?.free_uploads_remaining} remaining`}
              </p>
              <button
                onClick={() => navigate('/upload')}
                className="bg-ledger-orange text-white rounded-none hover:bg-ledger-orange/90 uppercase tracking-widest text-xs py-4 px-8 font-mono"
                data-testid="upload-new-btn"
              >
                <Upload className="w-4 h-4 inline mr-2" />
                {user?.subscription_status === 'active' ? 'Upload New Statement' : 'Try Free Extraction'}
              </button>
            </div>
          )}

          {/* Statement History */}
          <div className="border-2 border-black/10 bg-white p-8">
            <h2 className="font-mono text-sm uppercase tracking-widest text-ledger-green mb-6" data-testid="history-title">Recent Uploads</h2>
            
            {loading ? (
              <div className="text-center py-12 font-mono text-sm text-gray-600">Loading...</div>
            ) : statements.length === 0 ? (
              <div className="text-center py-12">
                <p className="font-mono text-sm text-gray-600 mb-4" data-testid="no-statements-msg">No statements uploaded yet</p>
                <button
                  onClick={() => navigate('/upload')}
                  className="bg-ledger-green text-white rounded-none hover:bg-ledger-green/90 uppercase tracking-widest text-xs py-3 px-6 font-mono"
                  data-testid="upload-first-btn"
                >
                  Upload Your First Statement
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full font-mono text-sm" data-testid="statements-table">
                  <thead>
                    <tr className="border-b border-black/10">
                      <th className="text-left py-3 px-4 uppercase tracking-widest text-xs text-gray-500">Status</th>
                      <th className="text-left py-3 px-4 uppercase tracking-widest text-xs text-gray-500">Filename</th>
                      <th className="text-left py-3 px-4 uppercase tracking-widest text-xs text-gray-500">Date</th>
                      <th className="text-left py-3 px-4 uppercase tracking-widest text-xs text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statements.map((statement) => (
                      <tr key={statement.id} className="border-b border-black/10" data-testid={`statement-row-${statement.id}`}>
                        <td className="py-3 px-4">{getStatusIcon(statement.status)}</td>
                        <td className="py-3 px-4">{statement.filename}</td>
                        <td className="py-3 px-4 text-gray-600">{formatDate(statement.upload_date)}</td>
                        <td className="py-3 px-4">
                          {statement.status === 'completed' ? (
                            <button
                              onClick={() => navigate(`/results/${statement.id}`)}
                              className="text-ledger-green hover:underline uppercase tracking-widest text-xs"
                              data-testid={`view-results-btn-${statement.id}`}
                            >
                              View Results
                            </button>
                          ) : statement.status === 'processing' ? (
                            <span className="text-yellow-600 uppercase tracking-widest text-xs">Processing...</span>
                          ) : (
                            <span className="text-red-600 uppercase tracking-widest text-xs">Failed</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;